import React from 'react';
import { useTranslation } from 'react-i18next';
import { GlobalDocumentIds } from '../../../types/GlobalDocumentIds';
import { TableOfContentsBlock } from '../../../_blocks/TableOfContents/TableOfContentsBlock';
import { AddContentBlock } from './partials/AddContentBlock/AddContentBlock';
import { useContentWrapper } from './useContentWrapper';
import {
  belowIsCollapsibleContainer,
  aboveIsCollapsibleContainer,
  nextBlockIsRichtext,
} from './ContentWrapper.utils';
import { isNotNullOrUndefined } from '../../../lib/typeGuards';
import { ContentWrapperContextProvider } from './ContentWrapperContext';
import { CollapsibleExpander } from '../../../_lib/components/CollapsibleExpander';
import { Namespace } from '../../../translation/namespace';
import { DragAndDropProvider } from '../../../_lib/DragAndDrop';
import { ContentWrapperDndProvider } from './ContentWrapperDndProvider';
import { BlockType } from '../../../types/api/ElementType';
import { LayoutGroup } from '../LayoutGroup/LayoutGroup';
import { VideoBlock } from '../../../_blocks/VideoBlock';
import { ContentImageBlock } from '../../../_blocks/ContentImage/ContentImageBlock';
import { RichTextBlock } from '../../../_blocks/RichText';
import { GenericSearchListBlock } from '../../../_blocks/GenericSearchList';
import { ChildPagesBlock } from '../../../_blocks/ChildPages/ChildPagesBlock';
import { PersonCollectionBlock } from '../../../_blocks/PersonCollection/PersonCollectionBlock';
import { ImageGalleryBlock } from '../../../_blocks/ImageGallery/ImageGallery';
import { ContentWrapperBlockProps, ContentWrapperBlocks } from './ContentWrapper.types';
import { TicketBookingBlock } from '../../../_blocks/TicketBooking/TicketBookingBlock';
import { isDynamicallyGeneratedBlockContent } from '../../../types/api/Block';
import { DynamicallyGeneratedContentHint } from '../../../_lib/components/Hint';
import { ExternalContentBlock } from '../../../_lib/components/ExternalContent';
import { SkipLink } from '../../../_lib/components/SkipLink';
import { CollapsibleContainerBlock } from '../../../_blocks/CollapsibleContainerBlock/CollapsibleContainerBlock';
import { PdfFileDetailsBlock } from '../../../_blocks/PdfFileDetails';
import { useAppContext } from '../../../contexts/AppContext/AppContext';
import bem from '../../../lib/bemUtils/bem';

// checking if a single block has edit view
const blocksContainEditView = (blocks: any) => blocks?.some((block: any) => block.embedded?.views?.edit);

export function ContentWrapper({
  fetchUrl,
  views,
  collapsibleParentUuid,
  moveOutsideOfCollapsibleBlock,
  registerStateHandler,
  onCollapsibleBlockContentWrapperChange,
  parentIndex,
}: ContentWrapperBlockProps &
  { fetchUrl: string,
    collapsibleParentUuid?: string,
    moveOutsideOfCollapsibleBlock?: (parentUuid: string | undefined, block: ContentWrapperBlocks, actionKey: string)
    => void,
    parentIndex?: number,
    registerStateHandler?: (uuid: string, callback: (block: ContentWrapperBlocks, actionKey: string) => void) => void,
    onCollapsibleBlockContentWrapperChange?: (
      collapsibleBlockUuid: string,
      collapsibleContentNumberOfBlocks: number,
    ) => void,
  }) {
  const { t } = useTranslation(Namespace.ContentWrapper);
  const addBlockView = views.addBlock;
  const tableOfContents = views.default.embedded?.tableOfContents;
  const { editModeInfo } = useAppContext();

  const {
    blocks,
    addBlock,
    error,
    setError,
    refetch,
    action,
    setAction,
    onDeleteSuccess,
    onMovingFailure,
    onMovingSuccess,
    moveBlockToBlock,
    submitNewBlockToBlockMove,
    createSnapshotOfBlockOrder,
    handleMoveBlockOutFromCollapsibleBlock,
    registerCollapsibleStateHandlerCallback,
    onConnectSuccess,
    collapsibleBlocksNumberOfBlocks,
    collapsibleBlocksNumberOfBlocksChanged,
  } = useContentWrapper(
    views?.default?.embedded?.content,
    fetchUrl,
    collapsibleParentUuid,
    moveOutsideOfCollapsibleBlock,
    registerStateHandler,
    onCollapsibleBlockContentWrapperChange,
  );

  const numberOfCollapsibleBlocks = blocks?.filter(
    block =>
      (
        block.elementType === BlockType.richtext &&
        block.fetchType === 'instant' &&
        block.embedded?.views?.default?.embedded?.collapsible) ||
        block.elementType === BlockType.collapsiblecontainer,
  )?.length;

  const getContentWrapperBlocks = (elementType: BlockType, block: any) => {
    if (isDynamicallyGeneratedBlockContent(block)) {
      let customHelpText;
      if (elementType === BlockType.ticketbooking) {
        customHelpText = t(`${Namespace.TicketBooking}:dynamicallyGeneratedBlockContent.explanation`);
      }
      return <DynamicallyGeneratedContentHint key={block.uuid} customHelpText={customHelpText} />;
    }

    switch (elementType) {
      case BlockType.video:
        return <VideoBlock key={block.uuid} {...block} />;
      case BlockType.contentimage:
        return <ContentImageBlock key={block.uuid} {...block} />;
      case BlockType.richtext:
        return <RichTextBlock key={block.uuid} {...block} />;
      case BlockType.genericsearchlist:
        return <GenericSearchListBlock key={block.uuid} {...block} />;
      case BlockType.childpages:
        return <ChildPagesBlock key={block.uuid} {...block} />;
      case BlockType.personcollection:
        return <PersonCollectionBlock key={block.uuid} {...block} />;
      case BlockType.imagegallery:
        return <ImageGalleryBlock key={block.uuid} {...block} />;
      case BlockType.layoutgroup:
        return <LayoutGroup key={block.uuid} {...block} />;
      case BlockType.ticketbooking:
        return <TicketBookingBlock key={block.uuid} {...block} />;
      case BlockType.externalcontent:
        return <ExternalContentBlock key={block.uuid} {...block} />;
      case BlockType.collapsiblecontainer:
        return <CollapsibleContainerBlock key={block.uuid} {...block} />;
      case BlockType.pdffiledetails:
        return <PdfFileDetailsBlock key={block.uuid} {...block} />;
      default:
        return null;
    }
  };

  return (
    <>
      {blocksContainEditView(blocks) && blocks.length > 2 && (
        <div className="content-wrapper__skip-link">
          <SkipLink id={GlobalDocumentIds.addContentBlock}>{t('skipLink') || ''}</SkipLink>
        </div>
      )}

      {/* rendering CollapsibleExpander only if
          - there is more than one collapsible block, because one alone can expand itself
          - and this is not a ContentWrapper inside another ContentWrapper
          - and we are not in editMode
      */}
      <CollapsibleExpander showExpander={numberOfCollapsibleBlocks > 1 && !parentIndex && !editModeInfo.active}>
        {isNotNullOrUndefined(tableOfContents) && !parentIndex && <TableOfContentsBlock {...tableOfContents} />}
        <div
          className={bem('content-wrapper', editModeInfo.active && collapsibleParentUuid ? 'child' : null)}
        >
          <div className="content-wrapper__inner">
            <DragAndDropProvider>
              {blocks?.map((block, index) => (
                <ContentWrapperContextProvider
                  key={block.uuid}
                  block={block}
                  index={index}
                  error={error}
                  action={action}
                  numBlocks={blocks.length}
                  setAction={setAction}
                  addBlock={addBlock}
                  setError={setError}
                  nextBlockIsRichtext={nextBlockIsRichtext(blocks, index)}
                  onDeleteSuccess={onDeleteSuccess}
                  onMovingFailure={onMovingFailure}
                  onMovingSuccess={onMovingSuccess}
                  refetch={refetch}
                  moveOutsideOfCollapsibleBlock={handleMoveBlockOutFromCollapsibleBlock}
                  parentIndex={parentIndex}
                  registerCollapsibleStateHandlerCallback={registerCollapsibleStateHandlerCallback}
                  belowIsCollapsibleContainer={belowIsCollapsibleContainer(blocks, index)}
                  aboveIsCollapsibleContainer={aboveIsCollapsibleContainer(
                    blocks,
                    index,
                    collapsibleBlocksNumberOfBlocks,
                  )}
                  onConnectSuccess={onConnectSuccess}
                  onCollapsibleBlockContentWrapperChange={collapsibleBlocksNumberOfBlocksChanged}
                >
                  <ContentWrapperDndProvider
                    block={block}
                    position={index + 1}
                    moveBlockToBlock={moveBlockToBlock}
                    submitNewBlockToBlockMove={submitNewBlockToBlockMove}
                    createSnapshotOfBlockOrder={createSnapshotOfBlockOrder}
                  >
                    {getContentWrapperBlocks(block.elementType, block)}
                  </ContentWrapperDndProvider>
                </ContentWrapperContextProvider>
              ))}
            </DragAndDropProvider>
          </div>
        </div>
      </CollapsibleExpander>
      {isNotNullOrUndefined(addBlockView) && (
        <AddContentBlock
          form={addBlockView.embedded.form}
          addBlock={addBlock}
          action={action}
          setAction={setAction}
          setError={setError}
          error={error}
          position={blocks.length}
        />
      )}
    </>
  );
}
